import { CONSTANTS } from '@pk/powerkioskutils';

export interface SupplierDashboardTimeRemaining {
	days: number;
	hours: number;
	minutes: number;
	seconds: number;
}

export interface SupplierDashboardSessionStatus {
	timeRemaining: SupplierDashboardTimeRemaining;
	timeRemainingDisplay: string;
	rfqSessionStatusColor: string;
}

function getTimeRemaining(effectiveEndDate: string): SupplierDashboardTimeRemaining {
	if (!effectiveEndDate) {
		return null;
	}

	const totalSeconds = Math.max(Math.floor((new Date(effectiveEndDate).getTime() - Date.now()) / 1000), 0);

	return {
		days: Math.floor(totalSeconds / 86400),
		hours: Math.floor(totalSeconds / 3600) % 24,
		minutes: Math.floor(totalSeconds / 60) % 60,
		seconds: totalSeconds % 60,
	};
}

function getTimeRemainingDisplay(timeRemaining: SupplierDashboardTimeRemaining, contractStatus: string): string {
	if (
		timeRemaining &&
		(timeRemaining.days > 0 || timeRemaining.hours > 0 || timeRemaining.minutes > 0 || timeRemaining.seconds > 0) &&
		![CONSTANTS.statuses.acomp, CONSTANTS.statuses.signed, CONSTANTS.statuses.confirmed].includes(Number(contractStatus))
	) {
		return `${timeRemaining.days}d ${timeRemaining.hours}h ${timeRemaining.minutes}m`;
	}

	return '0d 0h 0m';
}

function getRfqSessionStatusColor(session: { rfqSessionStatus: string; isOpenToBid: boolean; lifeTimeBids: number }): string {
	if (session.rfqSessionStatus === 'Won') {
		return 'yellow';
	} else if (!session.isOpenToBid) {
		return 'gray';
	} else if (session.rfqSessionStatus === 'Closing Today' || session.rfqSessionStatus === 'Extended') {
		return 'red';
	} else if (session.lifeTimeBids > 0) {
		return 'blue';
	} else {
		return 'green';
	}
}

/**
 * @param session.effectiveEndDate an ISO-8601 UTC timestamp computed by the API, so no timezone conversion is needed here
 */
export function getSupplierDashboardSessionStatus(session: {
	effectiveEndDate: string;
	contractStatus?: string;
	rfqSessionStatus: string;
	isOpenToBid: boolean;
	lifeTimeBids: number;
}): SupplierDashboardSessionStatus {
	const timeRemaining = getTimeRemaining(session.effectiveEndDate);

	return {
		timeRemaining,
		timeRemainingDisplay: getTimeRemainingDisplay(timeRemaining, session.contractStatus),
		rfqSessionStatusColor: getRfqSessionStatusColor(session),
	};
}
